import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CTABassProps {
  variant?: 'default' | 'waitlist';
  onGetStartedClick?: () => void;
  queued?: boolean;
  /** Accepted for call-site compatibility; the CTA is one responsive tree. */
  mobileRedesign?: boolean;
}

const CTABass = ({ variant = 'default', onGetStartedClick, queued = false }: CTABassProps) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const cta = (
    <section className="pt-[88px] pb-24 sm:py-32 bg-[#B45309] relative overflow-hidden border-t-0">
      {/* Background Depth - Original Orange Tones matching Hero */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_#D97706,_#B45309_60%,_#78350F_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(255,255,255,0.05),transparent_60%)]" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.15] mix-blend-overlay" />
      </div>

      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-center gap-4 sm:gap-10"
        >
          <div className="space-y-5 sm:space-y-6">
            <h2 className="text-[40px] sm:text-5xl md:text-6xl lg:text-7xl font-bold font-playfair text-[#F5F5F4] sm:text-white tracking-normal sm:tracking-tight leading-[1.08] sm:leading-[1.05] [text-wrap:balance] max-w-3xl mx-auto drop-shadow-md">
              Ready to love your <br /><span className="italic font-medium sm:font-bold">closet again?</span>
            </h2>
            <p className="text-white/90 text-[15px] sm:text-lg max-w-[320px] sm:max-w-xl mx-auto leading-[1.6] sm:leading-7 font-playfair pt-0 sm:pt-4 drop-shadow-sm">
              Join the professionals who have rediscovered their style with Cello. Get world-class editorial styling and digital closet curation.
            </p>
          </div>

          {variant === 'waitlist' ? (
            <div className="flex flex-col w-full max-w-lg mx-auto mt-4 pt-4">
              <div className="mb-8 w-full flex flex-col items-center">
                <p className="text-white/90 text-center text-[15px] sm:text-[16px] leading-relaxed font-playfair tracking-wide max-w-sm">
                  We are full at the moment.<br className="hidden sm:block" /> Join the waitlist for early access.
                </p>
              </div>
              <form
                onSubmit={async e => {
                  e.preventDefault();
                  if (!email) {
                    toast.error("Please enter your email");
                    return;
                  }
                  setIsSubmitting(true);
                  try {
                    const { error } = await supabase.from('waitlist_submissions').insert([{
                      email,
                      name: '',
                      comments: ''
                    }] as any);
                    if (error) throw error;
                    setHasSubmitted(true);
                    setEmail('');
                  } catch (error) {
                    console.error('Error submitting to waitlist:', error);
                    toast.error("Something went wrong. Please try again.");
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full"
              >
                {!hasSubmitted ? (
                  <>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full h-[56px] px-6 text-[13px] bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/30 focus:bg-white/10 transition-all rounded-md"
                      disabled={isSubmitting}
                    />
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-white hover:bg-stone-200 text-[#0a0502] px-8 h-[56px] rounded-md text-[12px] font-bold uppercase tracking-[0.15em] flex items-center justify-center shadow-2xl transition-all w-full sm:w-auto overflow-hidden whitespace-nowrap border-b-2 border-stone-300 active:border-b-0 active:translate-y-[2px]"
                    >
                      {isSubmitting ? 'Submitting...' : 'Request Access'}
                    </Button>
                  </>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full h-[56px] bg-white/10 border border-white/20 rounded-md flex items-center justify-center text-white text-sm font-medium tracking-wide shadow-inner backdrop-blur-sm"
                  >
                    <svg className="w-5 h-5 mr-2 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    You're on the list. We'll be in touch soon.
                  </motion.div>
                )}
              </form>
            </div>
          ) : (
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full sm:w-auto mt-4"
            >
              <Button
                onClick={() => onGetStartedClick ? onGetStartedClick() : navigate('/auth', { state: { tab: 'signup' } })}
                className={`px-10 h-[56px] rounded-md text-[12px] font-bold uppercase tracking-[0.15em] flex items-center justify-center shadow-none sm:shadow-2xl transition-all w-full sm:w-auto border-b-2 active:border-b-0 active:translate-y-[2px] gap-2 sm:gap-3 ${queued ? "bg-[#16A34A] hover:bg-[#15803D] text-white border-[#0f7a37]" : "bg-white hover:bg-stone-200 text-[#0f172a] sm:text-[#0a0502] border-stone-300"}`}
              >
                {queued ? "You're on the list" : "Join the waitlist"}
                {!queued && <span aria-hidden="true" className="sm:hidden">→</span>}
                {!queued && <ArrowRight className="hidden sm:block w-4 h-4 ml-1" strokeWidth={3} />}
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );

  // One responsive tree at every width. `mobileRedesign` stays on the props
  // (accepted, ignored) so the splash pages' call sites don't change.
  return cta;
};

export default CTABass;
